import { Box, Chip, CircularProgress, Typography } from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import useLogisticsTicket from "hooks/useLogisticsTicket";
import { PageActions, PageBreadcrumbs, PageSubtitle, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { PIPELINE_COLUMNS } from "../Pipeline/pipelineColumns";
import PipelineStageProgressBar from "./PipelineStageProgressBar";
import JobInformationCard from "./JobInformationCard";
import VendorManagementCard from "./VendorManagementCard";
import ScheduledPickupCard from "./ScheduledPickupCard";
import PickupCompletedCard from "./PickupCompletedCard";
import DeliveryVerifiedCard from "./DeliveryVerifiedCard";
import TimeTrackingCard from "./TimeTrackingCard";
import InvoiceCard from "./InvoiceCard";
import ActionsCard from "./ActionsCard";
import InternalNotesCard from "./InternalNotesCard";
import NeedsActionBadge from "../NeedsActionBadge";

export default function PipelinePickupDetailPage() {
  const { job, loading, error, refetch, applyTicket } = useLogisticsTicket();

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !job) {
    return (
      <Box sx={{ py: 8, textAlign: "center" }}>
        <Typography color="text.secondary">Unable to load pickup details. {error?.message}</Typography>
      </Box>
    );
  }

  const stageColumn = PIPELINE_COLUMNS.find((column) => column.stage === job.stage);
  const showScheduledPickup = job.stage === "scheduled_confirmed" && job.confirmation && job.currentAssignment;
  const showPickupCompleted = job.stage === "picked_up" && job.pickupCompletion && job.currentAssignment;
  const showDeliveryVerified = job.stage === "delivery_verified" && job.deliveryVerification && job.currentAssignment;

  return (
    <>
      <PageBreadcrumbs
        links={[
          { label: "Logistics", path: "/logistics_platform" },
          { label: "Pickups", path: "/logistics_platform" },
          { label: job.displayId },
        ]}
      />
      <PageTitle>
        <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1.5 }}>
          {job.displayId}
          {job.needsAction && <NeedsActionBadge canceled={!!job.cancellationReason} />}
        </Box>
      </PageTitle>
      <PageSubtitle>
        {job.companyName} — {job.city}, {job.state}
      </PageSubtitle>
      {stageColumn && (
        <PageActions>
          <Chip
            label={stageColumn.title.toUpperCase()}
            sx={{
              height: 28,
              fontWeight: 700,
              fontSize: 11,
              letterSpacing: 0.5,
              backgroundColor: stageColumn.badgeColor,
              color: stageColumn.badgeTextColor,
            }}
          />
        </PageActions>
      )}

      <Box sx={{ mt: 1, mb: 3 }}>
        <PipelineStageProgressBar currentStage={job.stage} />
      </Box>

      <Grid2 container spacing={2.5}>
        <Grid2 size={{ xs: 12, lg: 8 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <JobInformationCard job={job} onTicketUpdated={applyTicket} />
            {showDeliveryVerified ? (
              <DeliveryVerifiedCard job={job} />
            ) : showPickupCompleted ? (
              <PickupCompletedCard job={job} />
            ) : showScheduledPickup ? (
              <ScheduledPickupCard job={job} />
            ) : (
              <VendorManagementCard job={job} onQuotesRequested={refetch} />
            )}
            <InternalNotesCard job={job} onTicketUpdated={applyTicket} />
          </Box>
        </Grid2>
        <Grid2 size={{ xs: 12, lg: 4 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            <TimeTrackingCard job={job} />
            <InvoiceCard job={job} />
            <ActionsCard job={job} onChanged={refetch} />
          </Box>
        </Grid2>
      </Grid2>
    </>
  );
}
